// The conversation tab's centre (hero / transcript) and the bare thread the
// Studio shows. The surfaces are chat's own views (`ChatFullView`,
// `ChatThreadView`, modules/chat/view.tsx); this file projects the shell
// model into them.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { HeroToChatTransition } from "../../../components/HeroHost/HeroHost.js";
import { mkProposalIntents } from "../../../modules/chat/intents.js";
import { ChatFullView, ChatThreadView } from "../../../modules/chat.js";
import { readStatusOf } from "../../sourceReadStatus.js";
import { agentLinkResolver } from "../agentLinks.js";
import { useAppShell } from "../context.js";
import { spinnerSlot } from "./slots.js";

// What chat's views read and do, projected from the shell model: the same
// for the full surface and the bare thread.
const chatViewPropsOf = (
    shell: ReturnType<typeof useAppShell>,
): Omit<Parameters<typeof ChatFullView>[0], "conversationId"> => ({
    model: shell.chatState,
    context: {
        scope: shell.scope,
        anchor: shell.frame.anchor,
        refFor: shell.refFor,
        readStatusOf: (id) => readStatusOf(shell.library, id),
        resolveLink: agentLinkResolver(shell.refFor),
        sourceItems: shell.sourceItems,
        activeRef: shell.activeRef,
        composerCandidates: shell.composerCandidates,
        chatSlashCommands: shell.chatSlashCommands,
        ingest: shell.ingest,
        ingestConversations: shell.ingestConversations,
        failureText: shell.turnFailureText,
    },
    intents: { ...shell, ...mkProposalIntents(shell.sendChat) },
    host: {
        openFilePicker: shell.openFilePicker,
        changeMentions: shell.handleMentionsChange,
        goToChange: shell.goToChange,
        downloadExport: shell.downloadExportById,
        requestUpload: (sourceId, sourceTitle, conversationId) =>
            shell.requestUploadForSource(sourceId, sourceTitle, {
                conversationId,
            }),
        startNewConversation: shell.startNewConversation,
        // Same recovery as the boot gate's "Sign in again": a reload
        // re-runs the auth probe and lands on sign-in.
        signInAgain: () => window.location.reload(),
    },
    send: shell.sendChat,
});

// The conversation's full surface, so both the single-leaf `materialSlot` and
// a non-focused split pane's light body render the SAME element (the chat is
// global, not per-pane).
const ChatFullFor = ({
    conversationId,
}: {
    conversationId: string;
}): ReactNode => {
    const shell = useAppShell();
    return (
        <ChatFullView
            conversationId={conversationId}
            {...chatViewPropsOf(shell)}
        />
    );
};

// The conversation alone (the Studio's Chat): its messages, newest at the
// bottom, and a spinner while it loads; the Studio's own composer sends to
// it.
export const ConversationThread = ({
    conversationId,
}: {
    conversationId: string;
}): ReactNode => {
    const { t } = useLingui();
    const shell = useAppShell();
    return shell.conversationTranscriptPending(conversationId) ? (
        spinnerSlot(t`Loading conversation…`)
    ) : (
        <ChatThreadView
            conversationId={conversationId}
            {...chatViewPropsOf(shell)}
        />
    );
};

// The centre of a CONVERSATION tab — the ONE renderer that branches on the
// derived conversation state (hero-is-initial-conversation-state §4.6). It
// distinguishes THREE states (F76): a DURABLE conversation whose transcript
// is still loading → a loading spinner (NOT the hero — opening a durable
// conversation must never flicker the "new chat" hero); a genuinely-empty
// NASCENT conversation → the hero composer layout; a loaded transcript →
// the transcript. The SAME tab (same id) renders all three across its
// lifetime; the first send flips the nascent discriminant, never a tab
// kind. `HeroToChatTransition` (HeroHost.tsx) holds the hero mounted for
// ONE extra beat past that flip so its exit animation (composer glide +
// caption/CTA fade) can play — a pragmatic stand-in for a true
// shared-element composer (see that component's header). `key={conversationId}`
// matters: without it, switching tabs would reuse the transition's local
// "exiting" state across unrelated conversations.
export const ConversationCentre = ({
    conversationId,
}: {
    conversationId: string;
}): ReactNode => {
    const { t } = useLingui();
    const { conversationTranscriptPending, sessionFor, heroLayoutFor } =
        useAppShell();
    return conversationTranscriptPending(conversationId) ? (
        spinnerSlot(t`Loading conversation…`)
    ) : (
        <HeroToChatTransition
            key={conversationId}
            isEmpty={sessionFor(conversationId).conversation.length === 0}
            hero={(transition) => heroLayoutFor(conversationId, transition)}
            chat={() => <ChatFullFor conversationId={conversationId} />}
        />
    );
};
